import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, Button, TextInput, Spinner, Select, Tooltip } from "flowbite-react";
import type { FC } from "react";
import { useState, useEffect, useMemo } from "react";
import { HiOutlinePencilAlt, HiTrash, HiCheck, HiX, HiExclamationCircle, HiCurrencyDollar } from "react-icons/hi";
import * as workersService from "../../services/workers";
import * as rolesService from "../../services/roles";
import * as teamsService from "../../services/teams";
import { getCommissionLevels } from "../../services/commission-levels";
import TeamMultiSelector from "./TeamMultiSelector";
import CommissionOverridesModal from "./CommissionOverridesModal";
import type { Database } from "../../pages/types/supabase";
import type { WorkerWithRole } from "../../services/workers";
import type { CommissionLevel } from "../../types/commission-level";

type Role = Database["public"]["Tables"]["roles"]["Row"];
type WorkerUpdate = Database["public"]["Tables"]["workers"]["Update"];
type WorkerStatusFilter = "active" | "inactive";
const ALL_STATUS_FILTERS: WorkerStatusFilter[] = ["active", "inactive"];

type Team = {
  id: number;
  name: string;
  description: string | null;
  active: boolean;
};

interface WorkersTableProps {
  searchTerm?: string;
  currentPage?: number;
  usersPerPage?: number;
  statusFilter?: WorkerStatusFilter[];
  onFilteredCountChange?: (count: number) => void;
}

const WorkersTable: FC<WorkersTableProps> = function ({
  searchTerm = "",
  currentPage = 1,
  usersPerPage = 20, // Default value, can be adjusted
  statusFilter = ALL_STATUS_FILTERS,
  onFilteredCountChange,
}) {
  const [workers, setWorkers] = useState<WorkerWithRole[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [commissionLevels, setCommissionLevels] = useState<CommissionLevel[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedWorker, setEditedWorker] = useState<WorkerUpdate>({});
  const [editedTeams, setEditedTeams] = useState<number[]>([]);
  const [teamUpdateLoading, setTeamUpdateLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Overrides Modal State
  const [showOverridesModal, setShowOverridesModal] = useState(false);
  const [selectedWorkerForOverrides, setSelectedWorkerForOverrides] = useState<{id: number, name: string} | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [workersData, rolesData, teamsData, commissionLevelsData] = await Promise.all([
        workersService.getWorkers(),
        rolesService.getRoles(),
        teamsService.getAllTeams(),
        getCommissionLevels(),
      ]);
      setWorkers(workersData);
      setRoles(rolesData);
      setTeams(teamsData);
      setCommissionLevels(commissionLevelsData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Fehler beim Laden der Daten"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const handleEdit = (worker: WorkerWithRole) => {
    setEditingId(worker.id);
    setEditedWorker({
      full_name: worker.full_name,
      email: worker.email,
      role_id: worker.role_id,
      commission_level_id: worker.commission_level_id,
      revenue_goal_daily: worker.revenue_goal_daily,
      revenue_goal_month: worker.revenue_goal_month,
      active: worker.active,
    });
    setEditedTeams(worker.teams?.map(team => team.id) || []);
    setValidationError(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedWorker({});
    setEditedTeams([]);
    setValidationError(null);
    setTeamUpdateLoading(false);
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      setTeamUpdateLoading(true);
      setValidationError(null);
      
      // Validate team assignments first
      const isValidTeamAssignment = await workersService.validateTeamAssignments(editedTeams);
      if (!isValidTeamAssignment) {
        setValidationError("One or more selected teams are invalid. Please refresh and try again.");
        return;
      }

      // Update worker data and team assignments in a single operation
      await workersService.updateWorkerWithTeams(editingId, editedWorker, editedTeams);
      
      // Reset edit state
      setEditingId(null);
      setEditedWorker({});
      setEditedTeams([]);
      setValidationError(null);
      
      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error saving worker:", err);
      setValidationError(
        err instanceof Error
          ? err.message
          : "Fehler beim Aktualisieren des Mitarbeiters"
      );
    } finally {
      setTeamUpdateLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await workersService.deleteWorker(id);
      await fetchData();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Fehler beim Löschen des Mitarbeiters"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOverrides = (worker: WorkerWithRole) => {
    setSelectedWorkerForOverrides({
      id: worker.id,
      name: worker.full_name || "Unknown Worker"
    });
    setShowOverridesModal(true);
  };

  // Filter workers based on search term and status selection
  const filteredWorkers = useMemo(() => {
    const searchTermLower = searchTerm.trim().toLowerCase();

    return workers.filter((worker) => {
      const workerStatus: WorkerStatusFilter = worker.active ? "active" : "inactive";
      if (!statusFilter.includes(workerStatus)) {
        return false;
      }

      if (!searchTermLower) {
        return true;
      }

      // Search in full name
      if (
        worker.full_name &&
        worker.full_name.toLowerCase().includes(searchTermLower)
      ) {
        return true;
      }

      // Search in email
      if (
        worker.email &&
        worker.email.toLowerCase().includes(searchTermLower)
      ) {
        return true;
      }

      // Search in role name
      if (
        worker.roles &&
        worker.roles.role_name &&
        worker.roles.role_name.toLowerCase().includes(searchTermLower)
      ) {
        return true;
      }

      // Search in team names
      if (
        worker.teams &&
        worker.teams.some((team) =>
          team.name.toLowerCase().includes(searchTermLower)
        )
      ) {
        return true;
      }

      return false;
    });
  }, [workers, searchTerm, statusFilter]);

  // Paginate workers
  const paginatedWorkers = useMemo(() => {
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    return filteredWorkers.slice(startIndex, endIndex);
  }, [filteredWorkers, currentPage, usersPerPage]);

  useEffect(() => {
    if (loading) return;
    onFilteredCountChange?.(filteredWorkers.length);
  }, [filteredWorkers.length, onFilteredCountChange, loading]);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
        <div className="flex items-center gap-2 mb-2">
          <HiExclamationCircle className="h-5 w-5 flex-shrink-0" />
          <p className="font-medium">Fehler beim Laden der Daten</p>
        </div>
        <p className="text-sm mb-3">{error}</p>
        <Button color="failure" size="sm" onClick={fetchData}>
          <div className="flex items-center gap-2">
            {loading && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            )}
            Erneut versuchen
          </div>
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4">
      <Table
        hoverable
        striped
        className="dark:bg-gray-800 dark:text-gray-400"
        theme={{
          root: {
            shadow: "hidden",
            wrapper: "relative overflow-x-auto shadow-md sm:rounded-lg",
          },
        }}
      >
        <TableHead>
          <TableHeadCell>Vollständiger Name</TableHeadCell>
          <TableHeadCell>E-Mail</TableHeadCell>
          <TableHeadCell>Rolle</TableHeadCell>
          <TableHeadCell>Provisionslevel</TableHeadCell>
          <TableHeadCell>Teams</TableHeadCell>
          <TableHeadCell>Umsatzziel (Tag)</TableHeadCell>
          <TableHeadCell>Umsatzziel (Monat)</TableHeadCell>
          <TableHeadCell>Aktiv</TableHeadCell>
          <TableHeadCell>Aktionen</TableHeadCell>
        </TableHead>
        <TableBody>
          {paginatedWorkers.map((worker) => (
            <TableRow key={worker.id} className="bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
              <TableCell>
                {editingId === worker.id ? (
                  <TextInput
                    value={editedWorker.full_name ?? ""}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        full_name: e.target.value,
                      })
                    }
                  />
                ) : (
                  worker.full_name
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <TextInput
                    value={editedWorker.email ?? ""}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        email: e.target.value,
                      })
                    }
                  />
                ) : (
                  worker.email
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <Select
                    value={String(editedWorker.role_id ?? "")}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        role_id: Number(e.target.value),
                      })
                    }
                  >
                    <option value="">Rolle auswählen</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.role_name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  worker.roles.role_name || "Unbekannte Rolle"
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <Select
                    value={String(editedWorker.commission_level_id ?? "")}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        commission_level_id: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  >
                    <option value="" disabled>Level auswählen</option>
                    {commissionLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        Level {level.level_number} - {level.description}
                      </option>
                    ))}
                  </Select>
                ) : (
                  worker.commission_levels
                    ? `Level ${worker.commission_levels.level_number} - ${worker.commission_levels.description}`
                    : "Kein Level"
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <div className="min-w-[200px]">
                    <TeamMultiSelector
                      teams={teams}
                      selectedTeams={editedTeams}
                      onSelectionChange={setEditedTeams}
                      loading={teamUpdateLoading}
                      disabled={teamUpdateLoading}
                      placeholder="Select teams..."
                      className="w-full"
                    />
                    {validationError && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
                        <HiExclamationCircle className="h-3 w-3 flex-shrink-0" />
                        <span>{validationError}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {worker.teams && worker.teams.length > 0 ? (
                      worker.teams.map((team) => (
                        <span 
                          key={team.id} 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {team.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 text-sm">Keine Teams</span>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <TextInput
                    value={String(editedWorker.revenue_goal_daily ?? "")}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        revenue_goal_daily: Number(e.target.value),
                      })
                    }
                  />
                ) : (
                  worker.revenue_goal_daily
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <TextInput
                    value={String(editedWorker.revenue_goal_month ?? "")}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        revenue_goal_month: Number(e.target.value),
                      })
                    }
                  />
                ) : (
                  worker.revenue_goal_month
                )}
              </TableCell>
              <TableCell>
                {editingId === worker.id ? (
                  <input
                    type="checkbox"
                    aria-label={`Aktiv Status für ${worker.full_name}`}
                    checked={editedWorker.active ?? false}
                    onChange={(e) =>
                      setEditedWorker({
                        ...editedWorker,
                        active: e.target.checked,
                      })
                    }
                    className="float-left mr-2 h-5 w-5 cursor-pointer appearance-none rounded-sm border border-gray-300 bg-white bg-contain bg-center bg-no-repeat align-top checked:border-transparent checked:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  />
                ) : worker.active ? (
                  <HiCheck className="h-5 w-5 text-green-500" />
                ) : (
                  <HiX className="h-5 w-5 text-red-500" />
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-x-3">
                  {editingId === worker.id ? (
                    <>
                      <Button 
                        onClick={handleSave} 
                        color="success" 
                        size="sm"
                        disabled={teamUpdateLoading}
                      >
                        <div className="flex items-center gap-x-2">
                          {teamUpdateLoading ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          ) : (
                            <HiCheck className="text-lg" />
                          )}
                          {teamUpdateLoading ? "Speichern..." : "Speichern"}
                        </div>
                      </Button>
                      <Button
                        onClick={handleCancel}
                        color="failure"
                        size="sm"
                        disabled={teamUpdateLoading}
                      >
                        <div className="flex items-center gap-x-2">
                          <HiX className="text-lg" />
                          Abbrechen
                        </div>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => handleEdit(worker)}
                        color="primary"
                        size="sm"
                      >
                        <div className="flex items-center gap-x-2">
                          <HiOutlinePencilAlt className="text-lg" />
                          Bearbeiten
                        </div>
                      </Button>
                      <Tooltip content="Manage Commission Overrides">
                        <Button
                          onClick={() => handleOpenOverrides(worker)}
                          color="light"
                          size="sm"
                        >
                          <div className="flex items-center gap-x-2">
                            <HiCurrencyDollar className="text-lg" />
                          </div>
                        </Button>
                      </Tooltip>
                      <Button
                        onClick={() => handleDelete(worker.id)}
                        color="failure"
                        size="sm"
                      >
                        <div className="flex items-center gap-x-2">
                          <HiTrash className="text-lg" />
                          Löschen
                        </div>
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="mt-4 flex justify-end text-sm text-gray-700">
        {searchTerm
          ? `Gefundene Mitarbeiter: ${filteredWorkers.length} von ${workers.length}`
          : `Mitarbeiter insgesamt: ${workers.length}`}
      </div>

      {selectedWorkerForOverrides && (
        <CommissionOverridesModal
          show={showOverridesModal}
          onClose={() => setShowOverridesModal(false)}
          workerId={selectedWorkerForOverrides.id}
          workerName={selectedWorkerForOverrides.name}
        />
      )}
    </div>
  );
};

export default WorkersTable;
